import {
  DefaultError,
  useInfiniteQuery,
  useMutation,
  useQuery,
} from '@tanstack/react-query';
import { useInfiniteHandler, useQueryToast } from 'hooks';
import i18next from 'i18next';
import { apiClient } from 'utils';
import { getDefaultInfinitePageParam } from 'utils/service/defaultInfinitePageParam';
import {
  IResponseHandlerOptions,
  useResponseHandler,
} from 'utils/service/responseHandler';
import { ITemplatesLightListItem } from '../types';
import {
  IContractExtendedTemlatesInfiniteResponse,
  IContractExtendedTemlatesSearch,
  IContractExtendedTemplateListResponse,
  IContractTemlatesInfiniteResponse,
  IContractTemlatesSearch,
  IContractTemplateListResponse,
  IContractTemplateResponse,
  ICreateContractTemplate,
  IUpdateContractTemplate,
} from './types';

export const useGetContractTemplateLightList = (enabled?: boolean) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<ITemplatesLightListItem[], DefaultError>({
    queryKey: ['contract-template-light-list'],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `ContractTemplate/LightList`,
      });

      return responseHandler(response, {
        groupName: `useGetContractTemplateLightList`,
        notFoundPath: '/templates',
      });
    },
    enabled: enabled,
  });
};

export const useGetContractExtendedTemplates = (
  params?: IContractExtendedTemlatesSearch
) => {
  const { responseHandler } = useResponseHandler();
  const { getNextPageParam, setTotalPageCount } = useInfiniteHandler();

  const { itemCount, page } = getDefaultInfinitePageParam(
    params?.page,
    params?.itemCount
  );

  return useInfiniteQuery<
    IContractExtendedTemplateListResponse[],
    DefaultError
  >({
    queryKey: ['contract-extended-templates-infinite', params],
    queryFn: async ({ pageParam }) => {
      const response = await apiClient({
        method: 'GET',
        url: 'ContractTemplate/ExtendedTemplates',
        params: {
          ...JSON.parse(JSON.stringify(params)),
          itemCount,
          page: pageParam,
        },
      });

      const value =
        await responseHandler<IContractExtendedTemlatesInfiniteResponse>(
          response,
          {
            groupName: `useGetContractExtendedTemplates - ${pageParam}`,
          }
        );

      setTotalPageCount(value.totalPageCount);

      return value.templates;
    },
    initialPageParam: page,
    getNextPageParam: getNextPageParam,
  });
};

export const useGetContractTemplates = (params?: IContractTemlatesSearch) => {
  const { responseHandler } = useResponseHandler();
  const { getNextPageParam, setTotalPageCount } = useInfiniteHandler();

  const { itemCount, page } = getDefaultInfinitePageParam(
    params?.page,
    params?.itemCount
  );

  return useInfiniteQuery<IContractTemplateListResponse[], DefaultError>({
    queryKey: ['contract-templates-infinite', params],
    queryFn: async ({ pageParam }) => {
      const response = await apiClient({
        method: 'GET',
        url: 'ContractTemplate/Search',
        params: { ...params, itemCount, page: pageParam },
      });

      const value = await responseHandler<IContractTemlatesInfiniteResponse>(
        response,
        {
          groupName: `useGetTemplates - ${pageParam}`,
        }
      );

      setTotalPageCount(value.totalPageCount);

      return value.contractTemplates;
    },
    initialPageParam: page,
    getNextPageParam: getNextPageParam,
  });
};

export const useGetContractTemplate = (
  id: string,
  options?: IResponseHandlerOptions<IContractTemplateResponse>,
  enabled?: boolean,
  retry?: boolean
) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<IContractTemplateResponse, DefaultError>({
    queryKey: ['contract-template', id],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `ContractTemplate/${id}`,
      });

      return responseHandler(response, {
        groupName: `useGetContractTemplate - ${id}`,
        notFoundPath: '/templates',
        ...options,
      });
    },
    retry,
    enabled,
  });
};

export const usePostContractTemplate = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    'usePostContractTemplate'
  );
  return useMutation<string, DefaultError, ICreateContractTemplate>({
    async mutationFn(body) {
      loading(
        i18next.t('service-message.create.loading', {
          what: body.title,
        })
      );
      const response = await apiClient({
        method: 'POST',
        url: 'ContractTemplate',
        body: { contractTemplate: body },
      });

      return responseHandler(response, {
        groupName: 'usePostContractTemplate',
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.create.success', {
          what: '',
        })
      );
    },
    onError: dismiss,
  });
};

export const usePutContractTemplate = (contractTemplateId: string) => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('usePutContractTemplate');
  return useMutation<string, DefaultError, IUpdateContractTemplate>({
    async mutationFn(body) {
      loading(
        i18next.t('service-message.update.loading', {
          what: body.title,
        })
      );
      const response = await apiClient({
        method: 'PUT',
        url: 'ContractTemplate',
        body: { contractTemplate: body, contractTemplateId },
      });

      return responseHandler(response, {
        groupName: 'usePutContractTemplate',
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.update.success', {
          what: '',
        })
      );
    },
    onError: dismiss,
  });
};

export const useDeleteContractTemplate = (id: string) => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    'useDeleteContractTemplate'
  );
  return useMutation<string, DefaultError>({
    async mutationFn() {
      loading(
        i18next.t('service-message.remove.loading', {
          what: '',
        })
      );
      const response = await apiClient({
        method: 'DELETE',
        url: `ContractTemplate/${id}`,
      });

      return responseHandler(response, {
        groupName: 'useDeleteContractTemplate',
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.remove.success', {
          what: '',
        })
      );
    },
    onError: dismiss,
  });
};
